/**
 * CRM Client Subscription Page - View current plan, upgrade, manage billing.
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { cn } from '@/lib/utils';

export function CRMSubscriptionPage() {
  const { data: subData } = useQuery({
    queryKey: ['crm-subscription'],
    queryFn: () => crmPortalService.getSubscription(),
  });

  const { data: plansData } = useQuery({
    queryKey: ['crm-client-plans'],
    queryFn: () => crmPortalService.getClientPlans(),
  });

  const currentTier = subData?.tier || 'free';
  const plans = plansData?.plans || [];

  const tierOrder: Record<string, number> = { free: -1, Basic: 0, Standard: 1, Premium: 2 };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Your Subscription</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {currentTier === 'free'
            ? 'You are on the free tier. Upgrade to unlock AI insights, financial reports, and more.'
            : `You are on the ${currentTier} plan.`}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan: any) => {
          const isCurrent = plan.display_name === currentTier;
          const isUpgrade = (tierOrder[plan.display_name] || 0) > (tierOrder[currentTier] || -1);
          const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features || [];

          return (
            <Card key={plan.id} className={cn(
              'relative overflow-hidden transition-all',
              isCurrent ? 'border-[#5048e5] border-2' : 'hover:border-[#5048e5]/30'
            )}>
              {isCurrent && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#5048e5]" />
              )}
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">{plan.display_name}</h3>
                  {isCurrent && <Badge className="bg-[#5048e5]">Current</Badge>}
                </div>
                <p className="text-3xl font-bold mb-1">
                  ${parseFloat(plan.price_monthly).toFixed(2)}
                  <span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
                <p className="text-xs text-muted-foreground mb-4">{plan.description}</p>

                <div className="space-y-2 mb-4">
                  <PlanFeature label={plan.max_documents === -1 ? 'Unlimited documents' : `${plan.max_documents} documents`} included />
                  <PlanFeature label={plan.max_ai_queries === -1 ? 'Unlimited AI queries' : plan.max_ai_queries === 0 ? 'No AI queries' : `${plan.max_ai_queries} AI queries/mo`} included={plan.max_ai_queries !== 0} />
                  <PlanFeature label="Secure messaging" included />
                  <PlanFeature label="Document vault" included />
                  <PlanFeature label="Deadline tracker" included />
                  {features.includes('ai_chat') && <PlanFeature label="AI Business Chat" included />}
                  {features.includes('financial_reports') && <PlanFeature label="Financial reports" included />}
                  {features.includes('compliance_alerts') && <PlanFeature label="Compliance alerts" included />}
                  {features.includes('strategic_advisor') && <PlanFeature label="Strategic AI advisor" included />}
                  {features.includes('cash_flow_forecast') && <PlanFeature label="Cash flow forecast" included />}
                  {features.includes('scenario_modelling') && <PlanFeature label="Scenario modelling" included />}
                </div>

                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline">Current Plan</Button>
                ) : isUpgrade ? (
                  <Button className="w-full bg-[#5048e5] hover:bg-[#443fcc]">
                    Upgrade <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button disabled className="w-full" variant="outline">
                    <Lock className="h-4 w-4 mr-1" /> Downgrade
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PlanFeature({ label, included }: { label: string; included: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {included ? (
        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
      ) : (
        <Lock className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
      )}
      <span className={cn(included ? '' : 'text-muted-foreground/50')}>{label}</span>
    </div>
  );
}
